import React from 'react';
import { Tooltip } from '@mui/material';
import { type IProjectTaskModel } from '../models/ProjectTaskModel';
import { getClassByPriorityId } from '../models/TaskPriority';
import KanbanCardAvatar from './KanbanCardAvatar';
import { getNameByPriorityId } from '../models/TaskPriority';
import { useKanbanView } from '../contexts/KanbanViewContext';
import TaskProgressBar from './TaskProgressBar';
import { getFormattedDateOnly, isDateInPast } from '../../helpers/DateHelper.ts';
import {
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  CalendarToday as CalendarTodayIcon,
  PersonAddOutlined as PersonAddOutlinedIcon,
} from '@mui/icons-material';
import { AddDueDateButton } from './AddDueDateButton';

const TaskItem: React.FC<{
  task: IProjectTaskModel;
  editTaskRoute?: string;
  canOpenTaskDetail?: boolean;
}> = ({ task, editTaskRoute, canOpenTaskDetail = false }) => {
  const { openTaskDetail, openTaskAssignment, openAssignedUserPreview } = useKanbanView();

  const hasTimeEstimateInRelease =
    task.projectReleasesId !== 0 &&
    task.projectReleasesId !== null &&
    task.projectReleasesId !== undefined &&
    // Use release-specific values only when at least one of them is non-zero,
    // otherwise fall back to the global estimate/spent fields.
    ((task.taskEstimateTimeInRelease ?? 0) > 0 || (task.taskSpentTimeInRelease ?? 0) > 0);

  const progressEstimate = hasTimeEstimateInRelease
    ? (task?.taskEstimateTimeInRelease ?? 0)
    : (task?.taskEstimateTime ?? 0);
  const progressSpent = hasTimeEstimateInRelease
    ? (task?.taskSpentTimeInRelease ?? 0)
    : (task?.taskSpentTime ?? 0);
  const progressRemaining = hasTimeEstimateInRelease
    ? (task?.taskRemainingTimeInRelease ?? null)
    : (task?.taskRemainingTime ?? null);

  const overdueDueDate =
    task.taskDueDate != null && isDateInPast(task.taskDueDate) ? task.taskDueDate : null;
  const hasDueDate = Boolean(task.taskDueDate);

  return (
    <div
      className="kanban-card"
      style={{ cursor: 'pointer' }}
      onClick={() => {
        if (canOpenTaskDetail) {
          openTaskDetail(task.id, task.taskName ?? '');
        } else {
          console.warn(
            '[TaskItem] editTaskRouteId is missing/invalid - task detail is unavailable'
          );
        }
      }}
    >
      <div className="kanban-card__body">
        {/* Title (+ tag if present) — wrapped so we control the gap before the badges row */}
        <div className="kanban-card__header-block">
          <a
            className="kanban-card__title kanba-card__text card-title"
            href={editTaskRoute?.replace('#', task.id.toString()) || undefined}
            draggable={false}
            onClick={(e) => e.preventDefault()}
          >
            {task.taskName}
          </a>
          {task.taskTag && (
            <h6 className="kanban-card__text card-subtitle text-muted mt-0">#{task.taskTag}</h6>
          )}
        </div>

        {/* Priority -> type icon -> story points */}
        <div className="d-flex align-items-center mb-1 kanban-card__badges-row">
          <div className={`kanban-card__priority ${getClassByPriorityId(task?.priorityId ?? 0)}`}>
            {task?.priorityName ?? getNameByPriorityId(task?.priorityId ?? 0)}
          </div>
          {task.taskType && (
            <span className="kanban-card__type-icon" data-toggle="tooltip" title={task.taskType}>
              <img
                src={`/Content/icons/kanban/jira-icons/${task.taskType.toLowerCase()}.svg`}
                alt={task.taskType}
              />
            </span>
          )}
          {task.storyPoints && <div className="story-points">{task.storyPoints}</div>}
          {task.hasAiImplementationRun && (
            <span className="kanban-card__ai-badge" data-toggle="tooltip" title="AI used">
              AI
            </span>
          )}
        </div>

        {/* Progress bar */}
        <TaskProgressBar
          estimate={progressEstimate}
          spent={progressSpent}
          remaining={progressRemaining}
        />

        <hr className="kanban-card__line" />

        {/* Footer: comments, overdue date, assignee */}
        <div className="d-flex justify-content-between align-items-center kanban-card__footer-row">
          <div className="kanban-card__footer-item">
            {task.commentsCount > 0 && (
              <span data-toggle="tooltip" title={`${task.commentsCount} comments`}>
                <div className="kanban-card__stats">
                  <ChatBubbleOutlineIcon sx={{ fontSize: 11, mr: 0.5, display: 'block' }} />
                  {task.commentsCount}
                </div>
              </span>
            )}
          </div>

          <div className="kanban-card__footer-item">
            {overdueDueDate && (
              <div className="kanban-card__date-range kanban-card__due-date_past kanban-card__sm-text">
                <CalendarTodayIcon sx={{ fontSize: 11, mr: 0.5, display: 'block' }} />
                <span>{getFormattedDateOnly(overdueDueDate)}</span>
              </div>
            )}
          </div>

          <div className="kanban-card__footer-item">
            {!hasDueDate && <AddDueDateButton task={task} />}
            {task.assignedUser != null ? (
              <span
                role="button"
                tabIndex={0}
                data-toggle="tooltip"
                title={task.assignedUser?.fullName}
                aria-label={`View profile of ${task.assignedUser?.fullName ?? 'assigned user'}`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (task.assignedUser) {
                    openAssignedUserPreview(task.assignedUser, event.currentTarget);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  event.stopPropagation();
                  if (task.assignedUser) {
                    openAssignedUserPreview(task.assignedUser, event.currentTarget);
                  }
                }}
              >
                <KanbanCardAvatar
                  className="kanban-card__avatar kanban-card__task-avatar"
                  initials={task.assignedUser.initials ?? ''}
                  colorKey={task.assignedUser.fullName}
                  relationId={task.assignedUser.id}
                  hasProfilePhoto={task.assignedUser.hasProfilePhoto}
                  profilePhotoUrl={task.assignedUser.profilePhotoUrl ?? null}
                />
              </span>
            ) : (
              <Tooltip title="Assign user" arrow>
                <span
                  className="kanban-card__unassigned-avatar kanban-card__task-avatar"
                  aria-label="Assign user"
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();
                    openTaskAssignment(task.id, task.taskName ?? '', event.currentTarget);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    event.stopPropagation();
                    openTaskAssignment(task.id, task.taskName ?? '', event.currentTarget);
                  }}
                >
                  <PersonAddOutlinedIcon fontSize="inherit" />
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskItem;
